import React, { useState } from 'react';
import { useAppDataContext } from '../context/AppDataContext';
import { supabase } from '../supabaseClient';
import { Check, CreditCard, Shield, Star, ArrowRight, CheckCircle2 } from 'lucide-react';

const TherapistBilling: React.FC = () => {
    const { clinic, subscription } = useAppDataContext();
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const currentPlan = subscription?.plan_id || 'free';

    const handleUpgrade = async (planId: string) => {
        if (!clinic) return;
        setLoading(true);
        
        try {
            // Mock Checkout Process
            await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call

            let subId = subscription?.id;

            if (!subId) {
                // Create a new subscription record if none exists
                const { data, error } = await supabase
                    .from('subscriptions')
                    .insert([{ 
                        clinic_id: clinic.id, 
                        plan_id: planId,
                        status: 'active'
                    }])
                    .select('id')
                    .single();
                
                if (error) throw error;
                subId = data?.id;
            } else {
                // Update existing subscription
                const { error } = await supabase
                    .from('subscriptions')
                    .update({ plan_id: planId, status: 'active' })
                    .eq('id', subId);
                
                if (error) throw error;
            }

            // Update clinic tier for easy access
            await supabase
                .from('clinics')
                .update({ subscription_tier: planId })
                .eq('id', clinic.id);

            setShowSuccess(true);
            setTimeout(() => {
                window.location.reload(); // Reload to refresh context
            }, 2000);

        } catch (err) {
            console.error('Upgrade failed', err);
            alert('שגיאה בשדרוג המנוי. נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-full bg-background overflow-y-auto pb-24 dir-rtl scrollbar-hide text-foreground relative">
            {showSuccess && (
                <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-emerald-600 rounded-3xl p-8 flex flex-col items-center shadow-2xl border border-emerald-500 shadow-emerald-500/20 max-w-sm w-full text-center">
                        <CheckCircle2 size={64} className="text-white mb-4" />
                        <h2 className="text-3xl font-bold text-white mb-2">שדרוג בוצע בהצלחה!</h2>
                        <p className="text-emerald-100">מיד תועבר חזרה למערכת עם כל הפיצ'רים פתוחים.</p>
                    </div>
                </div>
            )}

            <header className="p-6 pt-10 bg-card border-b border-border sticky top-0 z-10">
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                    <CreditCard size={24} className="text-primary" /> מנוי ותשלומים
                </h1>
                <p className="text-muted-foreground text-sm mt-1">בחר את המסלול המתאים לקליניקה שלך</p>
            </header>

            <div className="p-6 grid gap-6 lg:grid-cols-2 lg:items-start">
                <div className="bg-card p-4 rounded-2xl border border-border flex items-center justify-between lg:col-span-2">
                    <div>
                        <p className="text-xs text-muted-foreground">המסלול הנוכחי שלך</p>
                        <p className="text-lg font-bold text-foreground uppercase tracking-wider">
                            {currentPlan === 'free' ? 'Free Plan' : currentPlan === 'pro' ? 'Pro Plan' : 'Clinic Plan'}
                        </p>
                    </div>
                    {currentPlan === 'free' ? (
                        <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-bold border border-border">בסיסי</div>
                    ) : (
                        <div className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/20">פעיל משודרג</div>
                    )}
                </div>

                {/* Free Tier */}
                <div className={`p-6 rounded-3xl border-2 transition-all ${currentPlan === 'free' ? 'border-border bg-muted/80' : 'border-border bg-card/50 opacity-70'}`}>
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-xl font-bold text-foreground">Free</h3>
                            <p className="text-muted-foreground text-sm">למטפלים בתחילת הדרך</p>
                        </div>
                        <div className="text-2xl font-bold text-foreground">
                            ₪0 <span className="text-sm font-normal text-muted-foreground">/ חודש</span>
                        </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                        <li className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Check size={18} className="text-muted-foreground mt-0.5 shrink-0" /> עד 3 מטופלים פעילים
                        </li>
                        <li className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Check size={18} className="text-muted-foreground mt-0.5 shrink-0" /> גרף מצב רוח ואנליטיקה בסיסית
                        </li>
                        <li className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Check size={18} className="text-muted-foreground mt-0.5 shrink-0" /> חלוקת משימות ידנית בלבד
                        </li>
                    </ul>

                    {currentPlan === 'free' ? (
                        <button disabled className="w-full py-3 rounded-xl bg-muted text-muted-foreground font-bold border border-border cursor-default">
                            המסלול הנוכחי
                        </button>
                    ) : (
                        <button onClick={() => handleUpgrade('free')} disabled={loading} className="w-full py-3 rounded-xl bg-muted hover:bg-muted text-foreground font-bold transition-colors">
                            הורד לשלב זה
                        </button>
                    )}
                </div>

                {/* Pro Tier */}
                <div className={`relative p-6 rounded-3xl border-2 transition-all overflow-hidden ${currentPlan === 'pro' ? 'border-primary bg-muted shadow-xl shadow-primary/20' : 'border-border bg-card/80 hover:border-primary/50'}`}>
                    {currentPlan !== 'pro' && (
                        <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">
                            מומלץ
                        </div>
                    )}
                    <div className="flex justify-between items-start mb-4 relative z-10">
                        <div>
                            <h3 className="text-xl font-bold text-primary flex items-center gap-2">
                                Pro <Star size={18} className="fill-primary" />
                            </h3>
                            <p className="text-muted-foreground text-sm">לקליניקה מתפתחת ועצמאית</p>
                        </div>
                        <div className="text-2xl font-bold text-foreground">
                            ₪149 <span className="text-sm font-normal text-muted-foreground">/ חודש</span>
                        </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6 relative z-10">
                        <li className="flex items-start gap-2 text-sm text-foreground">
                            <Check size={18} className="text-primary mt-0.5 shrink-0" /> <strong className="text-foreground">ללא הגבלת מטופלים</strong>
                        </li>
                        <li className="flex items-start gap-2 text-sm text-foreground">
                            <Check size={18} className="text-primary mt-0.5 shrink-0" /> <strong className="text-foreground">AI Assistant מלא:</strong> טיוטות משימות מבוססות סיכומי פגישה
                        </li>
                        <li className="flex items-start gap-2 text-sm text-foreground">
                            <Check size={18} className="text-primary mt-0.5 shrink-0" /> הערכת סיכון נטישה בזמן אמת
                        </li>
                        <li className="flex items-start gap-2 text-sm text-foreground">
                            <Check size={18} className="text-primary mt-0.5 shrink-0" /> דוחות חודשיים ב-PDF
                        </li>
                    </ul>

                    {currentPlan === 'pro' ? (
                        <button disabled className="w-full py-3 rounded-xl bg-primary/20 text-primary font-bold border border-primary/50 cursor-default relative z-10">
                            המסלול הנוכחי
                        </button>
                    ) : (
                        <button 
                            onClick={() => handleUpgrade('pro')} 
                            disabled={loading} 
                            className="w-full py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold transition-colors flex items-center justify-center gap-2 relative z-10 shadow-lg shadow-primary/30"
                        >
                            {loading ? 'מעבד...' : (
                                <>שדרג עכשיו (Mock) <ArrowRight size={18} className="rotate-180" /></>
                            )}
                        </button>
                    )}
                    
                    {/* Background glow */}
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
                </div>
                
                <div className="text-center p-4 rounded-xl bg-card border border-border lg:col-span-2">
                    <Shield className="mx-auto text-muted-foreground mb-2" size={24} />
                    <p className="text-xs text-muted-foreground">תשלום מאובטח. התוכנית מתחדשת אוטומטית. ניתן לבטל בכל עת.</p>
                </div>
            </div>
        </div>
    );
};

export default TherapistBilling;
